import { useContext } from "react";
import styled from "styled-components";
import { AnimatePresence, motion, Variants } from "framer-motion";
import { useToggle } from "react-use";

import { fill } from "../../styles/utils";

import BGImage from "../../assets/img/boots-merthyr-tydfil-store.jpg";
import { ImageFill } from "../ImageFill";

import { ShowIntroContext } from "../../context/IntroContext";
import { ExploreIntro } from "./ExploreIntro";
import { IntroSteps } from "./IntroSteps";
import { usePageReady } from "../../lib/hooks/use-page-ready";
import { Portal } from "../Portal";

interface IntroProps {
  showIntro: boolean;
}

const Wrapper = styled(motion.div)`
  ${fill("fixed")}
  z-index: 30;
`;

const BG = styled.div`
  ${fill()}
  opacity: 0.5;
`;

const DarkPrelay = styled.div`
  ${fill()}
  /* background-color: ${({ theme }) => theme.colors.blueDark}; */
  background-color: #02041F;
  z-index: -1;
`;

const BlurOverlay = styled(motion.div)`
  ${fill()}
  backdrop-filter: blur(8px);
`;

const blurVariants: Variants = {
  blur: { opacity: 1, transition: { duration: 1.6 } },
  normal: { opacity: 0, transition: { duration: 1.6 } },
};

const ButtonPosition = styled.div`
  position: absolute;
  bottom: 64px;
  left: 50%;
  transform: translate(-50%, 0);
  z-index: 2;
`;

export const Intro: React.FC<IntroProps> = ({ showIntro }) => {
  const [introDismissed, setIntroDismissed] = useToggle(false);
  const [isInTransition, setIsInTransition] = useToggle(false);
  const [pageReady] = usePageReady();

  const handleTextAnimateIn = () => {
    setIsInTransition(false);
  };

  const handleTextAnimateOut = () => {
    setIsInTransition(true);
  };

  const exploreClick = () => {
    setIntroDismissed(true);
  };

  return (
    <Portal>
      <AnimatePresence>
        {showIntro && !introDismissed && (
          <Wrapper
            key="intro"
            initial={false}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{
              type: "tween",
              duration: 2,
              delay: 0.5,
            }}
          >
            <BG>
              <ImageFill src={BGImage} />
            </BG>
            <DarkPrelay />
            <BlurOverlay
              variants={blurVariants}
              initial={false}
              animate={isInTransition ? "normal" : "blur"}
            />
            {pageReady && (
              <>
                <IntroSteps
                  onAnimateIn={handleTextAnimateIn}
                  onAnimateOut={handleTextAnimateOut}
                />

                <ButtonPosition>
                  <ExploreIntro onClick={exploreClick} />
                </ButtonPosition>
              </>
            )}
          </Wrapper>
        )}
      </AnimatePresence>
    </Portal>
  );
};
